/**
 * TranslateButton — one-click translation of a forum post or reply.
 * Uses /api/forum/translate. Caches result in a Map keyed by
 * `${textHash}::${lang}` so repeated clicks are free.
 */
import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Languages, Loader2 } from 'lucide-react';

const LANG_OPTIONS = [
  { value: 'English',    label: 'English' },
  { value: 'Spanish',    label: 'Espa\u00f1ol' },
  { value: 'French',     label: 'Fran\u00e7ais' },
  { value: 'German',     label: 'Deutsch' },
  { value: 'Portuguese', label: 'Portugu\u00eas' },
  { value: 'Chinese',    label: '\u4e2d\u6587' },
  { value: 'Japanese',   label: '\u65e5\u672c\u8a9e' },
  { value: 'Arabic',     label: '\u0627\u0644\u0639\u0631\u0628\u064a\u0629' },
  { value: 'Hindi',      label: '\u0939\u093f\u0928\u094d\u0926\u0940' },
  { value: 'Russian',    label: '\u0420\u0443\u0441\u0441\u043a\u0438\u0439' },
];

const cache = new Map(); // in-memory across component instances (per page load)
const hash = (s) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) + s.charCodeAt(i);
  return String(h);
};

const TranslateButton = ({ text, testid = 'translate-btn' }) => {
  const { API } = useContext(AuthContext);
  const [open, setOpen]         = useState(false);
  const [lang, setLang]         = useState('English');
  const [translation, setTranslation] = useState('');
  const [busy, setBusy]         = useState(false);

  if (!text || !text.trim()) return null;

  const run = async (target) => {
    const cacheKey = `${hash(text)}::${target}`;
    if (cache.has(cacheKey)) {
      setTranslation(cache.get(cacheKey));
      return;
    }
    try {
      setBusy(true);
      const { data } = await axios.post(`${API}/forum/translate`, {
        text, target_language: target,
      });
      const t = data?.translated || '';
      cache.set(cacheKey, t);
      setTranslation(t);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Translation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1 text-xs" data-testid={testid}>
      {!open && (
        <button
          type="button"
          onClick={() => { setOpen(true); run(lang); }}
          className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 font-bold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <Languages className="w-3 h-3" /> Translate
        </button>
      )}
      {open && (
        <div className="mt-1 rounded-lg border border-border bg-muted/40 p-2" data-testid={`${testid}-panel`}>
          <div className="flex items-center gap-2 mb-1">
            <Languages className="w-3 h-3 text-primary" />
            <select
              value={lang}
              onChange={(e) => { setLang(e.target.value); run(e.target.value); }}
              className="text-[11px] font-bold rounded border border-border bg-background px-1 py-0.5"
              data-testid={`${testid}-select`}
            >
              {LANG_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button
              type="button"
              onClick={() => { setOpen(false); setTranslation(''); }}
              className="ml-auto text-[10px] text-muted-foreground hover:text-foreground underline"
              data-testid={`${testid}-close`}
            >
              Show original
            </button>
          </div>
          {busy && (
            <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Translating{'\u2026'}
            </div>
          )}
          {!busy && translation && (
            <div className="text-sm text-foreground/90 whitespace-pre-line" data-testid={`${testid}-output`}>
              {translation}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TranslateButton;
